import {
  type BadgeTone,
  badgeToneClass,
} from "components/ui/StatusBadge/StatusBadge";
import { cn } from "lib/utils";
import type { HTMLAttributes } from "react";

/**
 * Badge の color プロパティ → StatusBadge の tone へのマッピング。
 * 旧 DaisyUI ベースの API を維持しつつ、内部的には自前のソフトパステル
 * トーン体系を使うため、画面全体で見た目が統一される。
 */
const COLOR_TO_TONE: Record<NonNullable<BadgeProps["color"]>, BadgeTone> = {
  default: "soft-neutral",
  primary: "tag-neutral",
  secondary: "tag-neutral",
  accent: "tag-neutral",
  ghost: "soft-neutral",
  info: "soft-info",
  success: "soft-success",
  warning: "soft-warning",
  error: "soft-error",
};

export type BadgeColor =
  | "default"
  | "primary"
  | "secondary"
  | "accent"
  | "ghost"
  | "info"
  | "success"
  | "warning"
  | "error";

export type BadgeProps = {
  color?: BadgeColor;
  size?: "xs" | "sm" | "md" | "lg";
  isOutline?: boolean;
} & Omit<HTMLAttributes<HTMLDivElement>, "className">;

/**
 * 共通 Badge リーフ。後方互換のために color/size/isOutline を露出するが、
 * 内部的には自前ソフトパステル tone (StatusBadge) を使うので、
 * 画面横断で badge の見た目が統一される。
 * @param {BadgeProps} props Badge の props
 * @return {JSX.Element} Badge 要素
 */
export const Badge = ({
  color = "default",
  children,
  ...rest
}: BadgeProps): JSX.Element => {
  const tone = COLOR_TO_TONE[color];
  return (
    <span className={cn(badgeToneClass[tone])} {...rest}>
      {children}
    </span>
  );
};
